import { Dialog } from "../../../../../common/core-ui/Dialog.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import {
  CoursesExtras,
  useCoursesExtras,
  useHomeContextUser,
} from "../../../homeContext.ts";
import { Suspense, useMemo } from "react";
import EditEdit from "../../../../../common/icons/EditEdit.tsx";
import SettingsLock from "../../../../../common/icons/SettingsLock.tsx";
import MiscellaneousSignOut from "../../../../../common/icons/MiscellaneousSignOut.tsx";
import BaseLinkOrButton from "../../../../../common/core-ui/BaseLinkOrButton.tsx";
import styles from "./MyAccount.module.scss";
import {
  DownloadApps,
  ManageDevices,
  QRMobileSignIn,
  ShippingAndTracking,
} from "./icons.tsx";
import { useBrandLoaderData } from "../../../../root/rootLoader.ts";
import UserAvatar from "../../../common/UserAvatar.tsx";
import { getAuthenticatedRedirectLink } from "../../../../user/redirect/redirectHelpers.ts";
import useReadOffline from "./read-offline/useReadOffline.ts";
import LanguagePicker from "../../../actions/user/LanguagePicker.tsx";
import useHasAccountCenterResources from "./useHasAccountCenterResources.ts";
import { getAlohaUrl } from "../../../../root/helpers/loginHandler.ts";
import { getLogoutUrl } from "common/api/decimal/methods/user.ts";
import { useCurrentFullLocale } from "../../../../../common/utils/locale.ts";
import { isInIframe } from "../../../../../common/utils/dom.ts";
import { Await, useAsyncValue } from "react-router-dom";
import DotLoader from "../../../../../common/core-ui/DotLoader.tsx";

function MyAccountInner({
  onOpenQrDialog,
  onOpenAppDialog,
  onOpenPublisherPrefsDialog,
  onRequestClose,
}: {
  onOpenQrDialog: () => void;
  onOpenAppDialog: () => void;
  onOpenPublisherPrefsDialog: () => void;
  onRequestClose: () => void;
}) {
  const intl = useIntl();
  const user = useHomeContextUser();
  const brand = useBrandLoaderData();
  const hasAccountCenterResources = useHasAccountCenterResources();
  const { launchApp } = useReadOffline();
  const locale = useCurrentFullLocale();
  const isSimplified = isInIframe();
  const { coursesLink, trackingAndShippingLink } =
    useAsyncValue() as CoursesExtras;

  const topLinks = useMemo(() => {
    return [
      {
        label: intl.formatMessage({ defaultMessage: "Edit Profile" }),
        icon: <EditEdit height={16} />,
        enabled: brand.AccountInfo.enabled && !user.ssoOnly && !isSimplified,
        href: getAuthenticatedRedirectLink(brand.AccountInfo.url),
      },
      {
        label: intl.formatMessage({ defaultMessage: "Reset Password" }),
        icon: <SettingsLock height={16} />,
        enabled: brand.ResetPassword.enabled && !user.ssoOnly && !isSimplified,
        href: getAuthenticatedRedirectLink(brand.ResetPassword.url),
      },
      {
        label: intl.formatMessage({ defaultMessage: "Complete Account" }),
        enabled:
          !brand.AccountCompletion.ssoOnly && user.ssoOnly && !isSimplified,
        href: getAlohaUrl("/user/sso"),
        target: "_self",
      },
    ].filter((el) => el.enabled);
  }, [intl, brand, user, isSimplified]);

  const logoutUrl = useMemo(() => {
    return getLogoutUrl();
  }, []);

  const bigBoxes = useMemo(() => {
    return [
      {
        label: intl.formatMessage({ defaultMessage: "Shipping & Tracking" }),
        icon: <ShippingAndTracking />,
        enabled: !!trackingAndShippingLink,
        href: trackingAndShippingLink,
      },
      {
        label: intl.formatMessage({ defaultMessage: "My Orders" }),
        icon: <ShippingAndTracking />,
        enabled: !!brand.VirtualStore.companyId,
        to: "/order-history",
        onClick: () => onRequestClose(),
      },
      {
        label: intl.formatMessage({ defaultMessage: "Download Apps" }),
        icon: <DownloadApps />,
        enabled: brand.DownloadOffline.enabled && !isSimplified,
        onClick: () => {
          launchApp();
          onOpenAppDialog();
        },
      },
      {
        label: intl.formatMessage({ defaultMessage: "Manage Devices" }),
        icon: <ManageDevices />,
        enabled: brand.Devices.enabled && !isSimplified,
        href: getAuthenticatedRedirectLink(brand.Devices.url),
      },
      {
        label: intl.formatMessage({ defaultMessage: "Quick Mobile Sign In" }),
        icon: <QRMobileSignIn />,
        enabled:
          brand.DownloadOffline.enabled &&
          brand.DownloadOffline.qrEnabled &&
          !isSimplified,
        onClick: onOpenQrDialog,
      },
      {
        label: intl.formatMessage({ defaultMessage: "Publisher Preferences" }),
        // svg icons are hidden, so this is too
        icon: (
          // eslint-disable-next-line formatjs/no-literal-string-in-jsx
          <span className={styles.emoji} aria-hidden="true">
            🔇
          </span>
        ),
        // Disabled until API ready
        // see useSupportsRecommendations
        // Will want to disable for isSimplified too
        enabled: false,
        onClick: onOpenPublisherPrefsDialog,
      },
    ].filter((el) => el.enabled);
  }, [
    intl,
    brand,
    launchApp,
    onOpenAppDialog,
    onOpenQrDialog,
    onOpenPublisherPrefsDialog,
    isSimplified,
    trackingAndShippingLink,
    onRequestClose,
  ]);

  const footerLinks = useMemo(() => {
    return [
      {
        label: intl.formatMessage({
          defaultMessage: "Browse Courses",
        }),
        href: coursesLink,
        enabled: !!coursesLink,
      },
      {
        label: intl.formatMessage({
          defaultMessage: "CA Notice of Collection",
        }),
        href:
          brand.NoticeCollection.url ||
          intl.formatMessage({
            defaultMessage:
              "https://support.vitalsource.com/hc/en-us/articles/360050635654-California-Notice-of-Information-Collection",
          }),
        enabled: brand.NoticeCollection.enabled,
      },
      {
        label:
          brand.About.label ||
          intl.formatMessage({
            defaultMessage: "About Us",
          }),
        href:
          brand.About.url ||
          intl.formatMessage({
            defaultMessage: "https://www.vitalsource.com/bookshelf-features",
          }),
        enabled: brand.About.enabled,
      },
      {
        label:
          brand.Privacy.label ||
          intl.formatMessage({
            defaultMessage: "Privacy Policy",
          }),
        href:
          brand.Privacy.url ||
          intl.formatMessage({
            defaultMessage:
              "https://support.vitalsource.com/hc/en-us/articles/201646123",
          }),
        enabled: brand.Privacy.enabled,
      },
      {
        label: intl.formatMessage({
          defaultMessage: "Resources",
        }),
        href: getAuthenticatedRedirectLink(brand.Resources.url),
        enabled: hasAccountCenterResources,
      },
      {
        label: intl.formatMessage({
          defaultMessage: "Cookie Settings",
        }),
        enabled: brand.Cookies.notify,
        onClick: () => {
          window.CookieCat?.showBanner?.({
            theme: brand.Theme.primary,
            locale: locale,
          });
          onRequestClose();
        },
      },
    ].filter((el) => el.enabled);
  }, [
    intl,
    brand,
    hasAccountCenterResources,
    onRequestClose,
    locale,
    coursesLink,
  ]);

  return (
    <>
      <UserAvatar className={styles.userAvatar} />
      <div className={styles.name}>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {user.ssoOnly ? user.reference : `${user.firstName} ${user.lastName}`}
      </div>
      <div className={styles.email}>{user.email}</div>
      {topLinks.length > 0 && (
        <div className={styles.topLinksRow}>
          {topLinks.map(({ label, icon, href, target }) => (
            <BaseLinkOrButton
              href={href}
              key={label}
              className="vst-link vst-link-size-small"
              target={target}
            >
              {icon}
              {label}
            </BaseLinkOrButton>
          ))}
        </div>
      )}
      {bigBoxes.length > 0 && (
        <div className={styles.boxRow}>
          {bigBoxes.map(({ label, icon, href, onClick, to }) => (
            <BaseLinkOrButton
              key={label}
              className={styles.boxLink}
              href={href}
              onClick={onClick}
              to={to}
            >
              {icon}
              {label}
            </BaseLinkOrButton>
          ))}
        </div>
      )}
      <LanguagePicker className={styles.languagePicker} />
      {brand.SignOut.enabled && !isSimplified && (
        <div className={styles.signOut}>
          <BaseLinkOrButton
            className="vst-button-pill"
            href={logoutUrl}
            target="_self"
          >
            <MiscellaneousSignOut />
            <FormattedMessage defaultMessage="Sign Out" />
          </BaseLinkOrButton>
        </div>
      )}
      <div className={styles.footerLinks}>
        {footerLinks.map(({ label, href, onClick }) => (
          <BaseLinkOrButton
            key={label}
            className="vst-link vst-link-size-small"
            href={href}
            onClick={onClick}
          >
            {label}
          </BaseLinkOrButton>
        ))}
      </div>
    </>
  );
}

export default function MyAccount({
  onRequestClose,
  onOpenQrDialog,
  onOpenAppDialog,
  onOpenPublisherPrefsDialog,
}: {
  onRequestClose: () => void;
  onOpenQrDialog: () => void;
  onOpenAppDialog: () => void;
  onOpenPublisherPrefsDialog: () => void;
}) {
  const intl = useIntl();
  const extras = useCoursesExtras();

  return (
    <Dialog
      onRequestClose={onRequestClose}
      darkHeader
      title={intl.formatMessage({ defaultMessage: "My Account" })}
    >
      <Suspense fallback={<DotLoader />}>
        <Await resolve={extras}>
          <MyAccountInner
            onOpenQrDialog={onOpenQrDialog}
            onOpenAppDialog={onOpenAppDialog}
            onRequestClose={onRequestClose}
            onOpenPublisherPrefsDialog={onOpenPublisherPrefsDialog}
          />
        </Await>
      </Suspense>
    </Dialog>
  );
}
